import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Store,
  Package,
  TriangleAlert as AlertTriangle,
  CircleCheck as CheckCircle2,
  PackagePlus,
  Printer,
  Loader as Loader2,
  ChevronDown,
  ChevronRight,
  Mail,
  RefreshCw,
  ScanLine,
  ClipboardList,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getAdminId } from '../../utils/getAdminId';
import { fetchCompanyProfile, type CompanyProfile } from '../../lib/companyProfile';
import {
  buildShopReceiptReportHtml,
  printShopReceiptReport,
  type ShopReceiptReportLine,
} from '../shop-terminal/shop-receipt-report';

interface ShopReceiptSubmission {
  receipt_id: string;
  reservation_id: string;
  reservation_number: string;
  customer_name: string | null;
  mode: 'one_by_one' | 'scan';
  submitted_at: string;
  submitted_by_name: string | null;
  warehouse_worker_name: string | null;
  notes: string | null;
  report_email_to: string | null;
  report_email_sent_at: string | null;
  report_email_error: string | null;
  report_printed_at: string | null;
  received_units: number;
  missing_units: number;
  extra_units: number;
  lines: ShopReceiptReportLine[];
}

function toIsoStart(value: string): string {
  return new Date(`${value}T00:00:00`).toISOString();
}

function toIsoEndExclusive(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  date.setDate(date.getDate() + 1);
  return date.toISOString();
}

function defaultFrom(): string {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().slice(0, 10);
}

/**
 * Shop-side confirmations of warehouse deliveries.
 *
 * Read-only by design. A discrepancy here is a record of what the shop counted,
 * not an instruction to change stock, so nothing on this screen writes to
 * inventory. Acting on a shortfall stays a deliberate admin decision.
 */
export const ShopReceiptsReport: React.FC = () => {
  const { user } = useAuth();
  const adminId = useMemo(() => (user ? getAdminId(user) : null), [user]);

  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState<ShopReceiptSubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [onlyDiscrepancies, setOnlyDiscrepancies] = useState(false);

  const load = useCallback(async () => {
    if (!adminId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('get_shop_receipt_submissions', {
        p_admin_id: adminId,
        p_from: toIsoStart(from),
        p_to: toIsoEndExclusive(to),
        p_limit: 500,
      });
      if (rpcError) throw rpcError;
      setRows((data ?? []) as ShopReceiptSubmission[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load shop confirmations');
    } finally {
      setLoading(false);
    }
  }, [adminId, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!adminId) return;
    let cancelled = false;
    void fetchCompanyProfile(adminId).then((profile) => {
      if (!cancelled) setCompany(profile);
    });
    return () => {
      cancelled = true;
    };
  }, [adminId]);

  const visible = useMemo(
    () => (onlyDiscrepancies ? rows.filter((r) => r.missing_units > 0 || r.extra_units > 0) : rows),
    [rows, onlyDiscrepancies]
  );

  const totals = useMemo(
    () => ({
      confirmations: visible.length,
      received: visible.reduce((sum, r) => sum + (r.received_units ?? 0), 0),
      missing: visible.reduce((sum, r) => sum + (r.missing_units ?? 0), 0),
      extra: visible.reduce((sum, r) => sum + (r.extra_units ?? 0), 0),
      withDiscrepancy: visible.filter((r) => r.missing_units > 0 || r.extra_units > 0).length,
    }),
    [visible]
  );

  const toggle = (receiptId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(receiptId)) next.delete(receiptId);
      else next.add(receiptId);
      return next;
    });
  };

  const reprint = (row: ShopReceiptSubmission) => {
    printShopReceiptReport(
      buildShopReceiptReportHtml({
        reservationNumber: row.reservation_number,
        customerName: row.customer_name,
        warehouseWorkerName: row.warehouse_worker_name,
        confirmedByName: row.submitted_by_name,
        mode: row.mode,
        submittedAt: row.submitted_at,
        lines: row.lines ?? [],
        company,
      })
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
            <Store className="h-7 w-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Shop Reservation Submissions
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              What the shop confirmed against what the warehouse sent. Record only, stock unchanged.
            </p>
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 sm:ml-auto cursor-pointer">
          <input
            type="checkbox"
            checked={onlyDiscrepancies}
            onChange={(e) => setOnlyDiscrepancies(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          Only show discrepancies
        </label>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Confirmations',
            value: totals.confirmations,
            icon: ClipboardList,
            tone: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
          },
          {
            label: 'Units Received',
            value: totals.received,
            icon: CheckCircle2,
            tone: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
          },
          {
            label: 'Units Missing',
            value: totals.missing,
            icon: AlertTriangle,
            tone: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
          },
          {
            label: 'Units Extra',
            value: totals.extra,
            icon: PackagePlus,
            tone: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
          },
        ].map((tile) => (
          <div
            key={tile.label}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
          >
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${tile.tone}`}>
              <tile.icon className="h-5 w-5" />
            </div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{tile.label}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{tile.value}</p>
          </div>
        ))}
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">Loading confirmations...</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              No confirmations in this range
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {onlyDiscrepancies
                ? 'Every delivery in this period matched what the warehouse sent.'
                : 'The shop has not confirmed any deliveries between these dates.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {visible.map((row) => {
              const isOpen = expanded.has(row.receipt_id);
              const clean = row.missing_units === 0 && row.extra_units === 0;
              return (
                <div key={row.receipt_id}>
                  <div className="p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                    <button
                      onClick={() => toggle(row.receipt_id)}
                      className="flex items-start gap-3 flex-1 min-w-0 text-left"
                    >
                      {isOpen ? (
                        <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-gray-900 dark:text-gray-100">
                            {row.reservation_number}
                          </span>
                          {clean ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                              Matched
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              Discrepancy
                            </span>
                          )}
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 flex items-center gap-1">
                            {row.mode === 'scan' ? (
                              <ScanLine className="h-3 w-3" />
                            ) : (
                              <ClipboardList className="h-3 w-3" />
                            )}
                            {row.mode === 'scan' ? 'Scanner' : 'One by one'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                          {row.customer_name || 'Customer'}
                          {row.submitted_by_name ? ` · confirmed by ${row.submitted_by_name}` : ''}
                          {row.warehouse_worker_name ? ` · picked by ${row.warehouse_worker_name}` : ''}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {new Date(row.submitted_at).toLocaleString()}
                          {row.report_email_sent_at && row.report_email_to && (
                            <span className="text-emerald-600 dark:text-emerald-400">
                              {' '}
                              · emailed to {row.report_email_to}
                            </span>
                          )}
                          {row.report_email_error && (
                            <span className="text-red-500"> · email failed</span>
                          )}
                          {row.report_printed_at && <span> · printed</span>}
                        </p>
                      </div>
                    </button>

                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                          {row.received_units} ok
                        </span>
                        {row.missing_units > 0 && (
                          <span className="text-amber-600 dark:text-amber-400 font-semibold">
                            {row.missing_units} missing
                          </span>
                        )}
                        {row.extra_units > 0 && (
                          <span className="text-violet-600 dark:text-violet-400 font-semibold">
                            {row.extra_units} extra
                          </span>
                        )}
                      </div>
                      <Button variant="secondary" size="sm" onClick={() => reprint(row)}>
                        <Printer className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Print</span>
                      </Button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="px-4 sm:px-5 pb-5 -mt-1">
                      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-700/50">
                            <tr>
                              <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">
                                Product
                              </th>
                              <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">
                                SKU
                              </th>
                              <th className="text-center px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">
                                Reserved
                              </th>
                              <th className="text-center px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">
                                Sent
                              </th>
                              <th className="text-center px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">
                                Received
                              </th>
                              <th className="text-center px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">
                                Missing
                              </th>
                              <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">
                                Counted by
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {(row.lines ?? []).map((line, index) => {
                              const variant = [line.color, line.size].filter(Boolean).join(' / ');
                              const isExtra = line.kind === 'extra';
                              return (
                                <tr
                                  key={`${row.receipt_id}-${index}`}
                                  className={
                                    isExtra
                                      ? 'bg-violet-50/50 dark:bg-violet-900/10'
                                      : line.missing_qty > 0 || line.reported_missing
                                      ? 'bg-amber-50/50 dark:bg-amber-900/10'
                                      : ''
                                  }
                                >
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                    {isExtra && (
                                      <span className="text-violet-600 dark:text-violet-400 font-semibold text-xs mr-1">
                                        EXTRA
                                      </span>
                                    )}
                                    {line.product_name || line.sku || 'Item'}
                                    {variant && (
                                      <span className="text-gray-500 dark:text-gray-400"> ({variant})</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">
                                    {line.sku || '-'}
                                  </td>
                                  <td className="px-3 py-2 text-center text-gray-600 dark:text-gray-400">
                                    {isExtra ? '-' : line.original_qty}
                                  </td>
                                  <td className="px-3 py-2 text-center text-gray-600 dark:text-gray-400">
                                    {isExtra ? '-' : line.expected_qty}
                                  </td>
                                  <td className="px-3 py-2 text-center font-semibold text-gray-900 dark:text-gray-100">
                                    {line.received_qty}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    {line.missing_qty > 0 ? (
                                      <span className="font-semibold text-amber-600 dark:text-amber-400">
                                        {line.missing_qty}
                                      </span>
                                    ) : line.reported_missing ? (
                                      <span className="text-amber-600 dark:text-amber-400 text-xs">
                                        never arrived
                                      </span>
                                    ) : (
                                      <span className="text-gray-300 dark:text-gray-600">-</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                                    {line.scan_source === 'epc'
                                      ? 'UHF tag'
                                      : line.scan_source === 'barcode'
                                      ? 'Barcode'
                                      : 'Manual'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {row.notes && (
                        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-semibold">Note:</span> {row.notes}
                        </p>
                      )}

                      {row.report_email_error && (
                        <p className="mt-3 text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
                          <Mail className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>Email to {row.report_email_to} failed: {row.report_email_error}</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {totals.withDiscrepancy > 0 && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {totals.withDiscrepancy} of {totals.confirmations} confirmations did not match. These are records of
            what the shop counted — no stock has been adjusted. Correcting inventory remains a separate,
            deliberate action.
          </p>
        </div>
      )}
    </div>
  );
};
